import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { fetcher } from 'shared/utils/fetcher';
import { shouldFilterError } from '../utils/shouldFilterError';

const APAC_COUNTRIES = ['AU', 'HK', 'SG', 'KR', 'TW', 'AE'];

export type CloudfrontLocation = {
  country: string;
  countryName: string;
  region: string;
  regionName: string;
  city: string;
  postalCode: string;
  latitude: string;
  longitude: string;
  timeZone: string;
  isAPAC: boolean;
};

const cloudfrontLocationQueryFn = async (): Promise<CloudfrontLocation | null> => {
  try {
    const { headers } = await fetcher.get('/apis-edge/location');

    const country = headers['cloudfront-viewer-country'] || '';
    const isAPAC = APAC_COUNTRIES.includes(country);

    return {
      country,
      countryName: headers['cloudfront-viewer-country-name'] || '',
      region: headers['cloudfront-viewer-country-region'] || '',
      regionName: headers['cloudfront-viewer-country-region-name'] || '',
      city: headers['cloudfront-viewer-city'] || '',
      postalCode: headers['cloudfront-viewer-postal-code'] || '',
      latitude: headers['cloudfront-viewer-latitude'] || '',
      longitude: headers['cloudfront-viewer-longitude'] || '',
      timeZone: headers['cloudfront-viewer-time-zone'] || '',
      isAPAC
    };
  } catch (e: any) {
    // Only log if it’s not a known network noise error
    if (!shouldFilterError(e)) {
      datadogRum.addError(e);
    }
    return null;
  }
};

const cloudfrontLocationQueryKey = ['cloudfrontLocation'];

export const useCloudfrontLocationQuery = () => {
  const country = Cookies.get('vercel_country') || '';
  const isAPAC = APAC_COUNTRIES.includes(country);

  const initialData = {
    country,
    countryName: '',
    region: Cookies.get('vercel_region') || '',
    regionName: '',
    city: Cookies.get('vercel_city') || '',
    postalCode: Cookies.get('vercel_postal_code') || '',
    latitude: Cookies.get('vercel_latitude') || '',
    longitude: Cookies.get('vercel_longitude') || '',
    timeZone: Cookies.get('vercel_time_zone') || ''
  };

  const hasVercelData = Object.values(initialData).some((value) => value !== '');

  return useQuery({
    // Use cookies from Vercel via middleware if available to not block renders waiting for Cloudfront location
    initialData: hasVercelData
      ? {
          ...initialData,
          isAPAC
        }
      : null,
    queryKey: cloudfrontLocationQueryKey,
    queryFn: cloudfrontLocationQueryFn
  });
};
